import { addFolderLink, createWidgetForPage, DEFAULT_WIDGET_PAGE_ID, getWidgetsByPage, updateFolder, getAllFolders, getDataItem, addDataItem, addFolder, getFolderLink, updateFolderLink } from '@/lib/db';
import { emitDataChanged } from '@/hooks/useAppData';
import type { DragPayload } from '@/components/shared/DndProvider';
import type { DataItem, FolderDataLink } from '@/types';
import { toast } from 'sonner';
import { folderBus } from '@/events/folderBus';

/** Duplicate a DataItem with a new ID */
async function duplicateDataItem(dataId: string): Promise<DataItem | null> {
  const original = await getDataItem(dataId);
  if (!original) return null;
  const copy: DataItem = {
    ...original,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await addDataItem(copy);
  return copy;
}

/** Check if targetId is a descendant of folderId (prevents circular nesting) */
async function isDescendant(folderId: string, targetId: string): Promise<boolean> {
  if (folderId === targetId) return true;
  const folders = await getAllFolders();
  const visited = new Set<string>();
  let current = targetId;
  while (current) {
    if (visited.has(current)) break;
    visited.add(current);
    if (current === folderId) return true;
    const f = folders.find((fo) => fo.id === current);
    if (!f || !f.parentId) break;
    current = f.parentId;
  }
  return false;
}

/**
 * Central handler for cross-area drag & drop.
 */
export async function handleCrossAreaDrop(
  payload: DragPayload,
  targetArea: string,
  targetFolderId?: string,
  targetPageId = DEFAULT_WIDGET_PAGE_ID,
): Promise<void> {
  const { sourceArea, dataIds } = payload;
  const folderIds = payload.folderIds ?? [];

  if (dataIds.length === 0 && folderIds.length === 0) return;

  // ── Folder → Root (move to top level) ──
  if (sourceArea === 'folder' && targetArea === 'folder-root') {
    const allFolders = await getAllFolders();

    // Move folders to root
    if (folderIds.length > 0) {
      for (const fid of folderIds) {
        const folder = allFolders.find((f) => f.id === fid);
        if (!folder || folder.parentId === null) continue;
        await updateFolder({ ...folder, parentId: null });
      }
    }

    // Move folder items to root
    if (dataIds.length > 0) {
      // Look for or create "이동된 항목" folder at root
      let targetFolder = allFolders.find(f => f.parentId === null && f.name === '이동된 항목');
      if (!targetFolder) {
        targetFolder = {
          id: crypto.randomUUID(),
          name: '이동된 항목',
          parentId: null,
          order: Date.now(),
          createdAt: Date.now(),
        };
        await addFolder(targetFolder);
      }

      for (const linkId of payload.itemIds) {
        const link = await getFolderLink(linkId);
        if (link) {
          await updateFolderLink({ ...link, folderId: targetFolder.id });
        }
      }
    }

    emitDataChanged();
    toast.success(`항목을 루트로 이동했습니다`);
    return;
  }

  // ── Widget/History → Folder-Root (duplicate to root level, no folder) ──
  if ((sourceArea === 'widget' || sourceArea === 'history') && targetArea === 'folder-root' && dataIds.length > 0) {
    const now = Date.now();
    const autoFolder = {
      id: crypto.randomUUID(),
      name: `드롭 항목 ${new Date(now).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}`,
      parentId: null,
      order: now,
      createdAt: now,
    };
    await addFolder(autoFolder);

    let addedCount = 0;
    for (let i = 0; i < dataIds.length; i++) {
      const copy = await duplicateDataItem(dataIds[i]);
      if (!copy) continue;
      const link: FolderDataLink = {
        id: crypto.randomUUID(),
        folderId: autoFolder.id,
        dataId: copy.id,
        order: now + i,
      };
      await addFolderLink(link);
      addedCount++;
    }

    emitDataChanged();
    toast.success(`${addedCount}개 항목을 새 폴더에 복제했습니다`);
    return;
  }

  // ── Folder → Folder (reparenting / moving) ──
  if (sourceArea === 'folder' && targetArea === 'folder' && targetFolderId) {
    const allFolders = await getAllFolders();

    // Move folders
    if (folderIds.length > 0) {
      for (const fid of folderIds) {
        if (fid === targetFolderId) continue;
        if (await isDescendant(fid, targetFolderId)) {
          toast.error('하위 폴더로 이동할 수 없습니다');
          return;
        }
        const folder = allFolders.find((f) => f.id === fid);
        if (!folder) continue;
        await updateFolder({ ...folder, parentId: targetFolderId });
      }
    }

    // Move folder items (links)
    if (payload.itemIds.length > 0 && dataIds.length > 0) {
      for (const linkId of payload.itemIds) {
        const link = await getFolderLink(linkId);
        if (link) {
          await updateFolderLink({ ...link, folderId: targetFolderId });
        }
      }
    }

    emitDataChanged();
    toast.success(`항목을 이동했습니다`);

    if (targetFolderId) {
      folderBus.emitFolderOpen(targetFolderId);
    }
    return;
  }

  // ── Widget/History → Folder (duplicate data) ──
  if ((sourceArea === 'widget' || sourceArea === 'history') && targetArea === 'folder') {
    if (!targetFolderId) {
      console.warn('[dnd] dropped on folder area but no targetFolderId');
      return;
    }

    let addedCount = 0;
    for (let i = 0; i < dataIds.length; i++) {
      const copy = await duplicateDataItem(dataIds[i]);
      if (!copy) continue;

      const link: FolderDataLink = {
        id: crypto.randomUUID(),
        folderId: targetFolderId,
        dataId: copy.id,
        order: Date.now() + i,
      };
      await addFolderLink(link);
      addedCount++;
    }

    emitDataChanged();
    toast.success(`${addedCount}개 항목을 폴더에 복제했습니다`);
    
    if (targetFolderId) {
      folderBus.emitFolderOpen(targetFolderId);
    }
    return;
  }

  // ── Folder → Widget (duplicate data) ──
  if (sourceArea === 'folder' && targetArea === 'widget') {
    const existing = await getWidgetsByPage(targetPageId);
    const minOrder = existing.length > 0 ? Math.min(...existing.map(w => w.order)) : 0;

    let addedCount = 0;
    for (let i = 0; i < dataIds.length; i++) {
      const copy = await duplicateDataItem(dataIds[i]);
      if (!copy) continue;

      await createWidgetForPage(copy.id, targetPageId, minOrder - 1 - i);
      addedCount++;
    }

    emitDataChanged();
    toast.success(`${addedCount}개 항목을 위젯에 복제했습니다`);
    return;
  }

  // ── History → Widget (duplicate data) ──
  if (sourceArea === 'history' && targetArea === 'widget') {
    const existing = await getWidgetsByPage(targetPageId);
    const minOrder = existing.length > 0 ? Math.min(...existing.map(w => w.order)) : 0;

    let addedCount = 0;
    for (let i = 0; i < dataIds.length; i++) {
      const copy = await duplicateDataItem(dataIds[i]);
      if (!copy) continue;

      await createWidgetForPage(copy.id, targetPageId, minOrder - 1 - i);
      addedCount++;
    }

    emitDataChanged();
    toast.success(`${addedCount}개 항목을 위젯에 복제했습니다`);
    return;
  }

  console.log('[dnd] unhandled drop', { sourceArea, targetArea, targetFolderId });
}
